import { platformApiClient } from './platform-api-client';
import type {
  SubscriptionDetail,
  SubscriptionListParams,
  SubscriptionPage,
  SubscriptionTransitionResult,
} from '@/types/platform-subscription.types';

/**
 * Phase 12.4E — the Subscriptions API client. Authorized by **D-109 §1**.
 *
 * ── Envelope reminder ───────────────────────────────────────────────────────
 * `platformApiClient` already unwraps `{ data, meta }`, so `r.data` **is** the
 * payload. A second `.data` access returns undefined — see CLAUDE.md.
 *
 * ── The only field any lifecycle request may carry is `reason` ──────────────
 * Not `tenantId`, `planId`, `status`, `period`, `expiresAt`, `statusChangedBy`
 * or `statusChangedAt`. The subscription is identified by the **URL**. This is
 * not merely convention: the backend runs `forbidNonWhitelisted`, so an extra
 * property is rejected with 400 rather than ignored — which is exactly what
 * should happen if this file ever drifts.
 *
 * There is no `create`, no `delete` and no generic `PATCH` here, because no such
 * route exists. Every write is a named transition.
 */
export const platformSubscriptionService = {
  /**
   * The list: one row per company — that company's canonical current
   * subscription (OD-1), newest first, server-side paged.
   *
   * Guarded with `Array.isArray` per the response-envelope convention: a shape
   * surprise degrades to an empty page rather than throwing inside a render.
   */
  list: (params: SubscriptionListParams = {}): Promise<SubscriptionPage> =>
    platformApiClient
      .get<SubscriptionPage>('/platform/subscriptions', { params })
      .then((r): SubscriptionPage => {
        const page = r.data;
        return {
          items: Array.isArray(page?.items) ? page.items : [],
          total: typeof page?.total === 'number' ? page.total : 0,
          limit: typeof page?.limit === 'number' ? page.limit : 0,
          offset: typeof page?.offset === 'number' ? page.offset : 0,
        };
      }),

  /** Detail, including the company's prior subscription rows. */
  getById: (subscriptionId: string): Promise<SubscriptionDetail> =>
    platformApiClient
      .get<SubscriptionDetail>(`/platform/subscriptions/${subscriptionId}`)
      .then((r) => r.data),

  // ── Lifecycle (Phase 12.4D, SUBSCRIPTIONS_DESIGN.md §6.3) ─────────────────
  //
  // Four named actions, not one `PATCH { status }`. The backend owns the state
  // machine, so the console cannot express an illegal transition — there is no
  // route for one. All four require `platform:subscriptions:manage`.

  /**
   * TRIAL → ACTIVE. Reason optional.
   *
   * Sending `{ reason: undefined }` serialises to `{}`, which the backend
   * accepts — and an omitted reason **clears** any previous one (OD-D). That is
   * a real side effect, so the dialog says so rather than leaving it implicit.
   */
  activate: (subscriptionId: string, reason?: string): Promise<SubscriptionTransitionResult> =>
    platformApiClient
      .post<SubscriptionTransitionResult>(
        `/platform/subscriptions/${subscriptionId}/activate`,
        reason ? { reason } : {},
      )
      .then((r) => r.data),

  /** ACTIVE → SUSPENDED. Reason MANDATORY — the company's users lose access. */
  suspend: (subscriptionId: string, reason: string): Promise<SubscriptionTransitionResult> =>
    platformApiClient
      .post<SubscriptionTransitionResult>(`/platform/subscriptions/${subscriptionId}/suspend`, {
        reason,
      })
      .then((r) => r.data),

  /** SUSPENDED → ACTIVE. Reason optional; omitting it clears the suspension reason. */
  reactivate: (subscriptionId: string, reason?: string): Promise<SubscriptionTransitionResult> =>
    platformApiClient
      .post<SubscriptionTransitionResult>(
        `/platform/subscriptions/${subscriptionId}/reactivate`,
        reason ? { reason } : {},
      )
      .then((r) => r.data),

  /** TRIAL | ACTIVE | SUSPENDED → CANCELLED. **Terminal.** Reason MANDATORY. */
  cancel: (subscriptionId: string, reason: string): Promise<SubscriptionTransitionResult> =>
    platformApiClient
      .post<SubscriptionTransitionResult>(`/platform/subscriptions/${subscriptionId}/cancel`, {
        reason,
      })
      .then((r) => r.data),
};
